/**
 * useGraphData - Hook for fetching relationship graph data
 *
 * Fetches people, edges, and stats from the archive via electronArchive IPC.
 *
 * @module @humanizer/studio/hooks/useGraphData
 */

import { useState, useCallback, useEffect, useRef } from 'react';

export interface GraphPerson {
  id: string;
  name: string;
  isFriend: boolean;
  friendSince?: number;
  isFollower: boolean;
  isFollowing: boolean;
  interactionCount: number;
  tagCount: number;
  relationshipStrength: number;
  edgeCount: number;
}

export interface PersonEdge {
  type: string;
  weight: number;
  count: number;
}

export interface PersonContent {
  id: string;
  text?: string;
  title?: string;
  sourceType?: string;
  threadRootId?: string;
  timestamp?: number;
  relationshipType: string;
}

export interface PersonContext {
  person: GraphPerson;
  edges: PersonEdge[];
  recentContent: PersonContent[];
}

export interface GraphDataOptions {
  sortBy?: 'strength' | 'name' | 'recent';
  friendsOnly?: boolean;
  search?: string;
  limit?: number;
}

export interface GraphDataReturn {
  people: GraphPerson[];
  loading: boolean;
  error: string | null;
  selectedPerson: PersonContext | null;
  selectedPersonLoading: boolean;
  fetch: (options?: GraphDataOptions) => Promise<void>;
  selectPerson: (personId: string) => Promise<void>;
  clearSelection: () => void;
}

export function useGraphData(): GraphDataReturn {
  const [people, setPeople] = useState<GraphPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<PersonContext | null>(null);
  const [selectedPersonLoading, setSelectedPersonLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const fetch = useCallback(async (options?: GraphDataOptions) => {
    if (!window.electronArchive) {
      setError('Archive not available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await window.electronArchive.query('getGraphData', [options || {}]) as any;
      if (mountedRef.current) {
        setPeople(result.people || []);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch graph data');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const selectPerson = useCallback(async (personId: string) => {
    if (!window.electronArchive) return;

    setSelectedPersonLoading(true);
    try {
      const result = await window.electronArchive.query('getPersonContext', [personId]) as any;
      if (mountedRef.current) {
        setSelectedPerson(result);
      }
    } catch (err) {
      console.error('[useGraphData] Failed to fetch person context:', err);
    } finally {
      if (mountedRef.current) setSelectedPersonLoading(false);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedPerson(null);
  }, []);

  return {
    people,
    loading,
    error,
    selectedPerson,
    selectedPersonLoading,
    fetch,
    selectPerson,
    clearSelection,
  };
}
